import React, { useState, useEffect, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Col, Row, Card, CardBody, CardHeader, Button } from "reactstrap";
import { Fn_AddEditData, Fn_DisplayData } from "../../store/Functions";
import { useDispatch } from "react-redux";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useLocation, useNavigate } from "react-router-dom";
import { Save, ArrowLeft, Tag, Settings, DollarSign, Calendar, FileText } from "react-feather";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { formatDateYYYYMMDD } from "../../helpers/dateUtils";

const AddEdit_CouponMaster = () => {
  const API_URL_SAVE = "CouponMaster/0/token";
  const API_URL_EDIT = API_WEB_URLS.MASTER + "/0/token/CouponMaster/Id";
  
  const breadCrumbTitle = "Coupon Master";
  const breadcrumbItem = "Coupon Master";

  // Type dropdown options
  const typeOptions = [
    { Id: 1, Name: 'Flat' },
    { Id: 2, Name: 'Percent' }
  ];

  // IsAvailableForAll dropdown options
  const isAvailableForAllOptions = [
    { Id: 1, Name: 'Yes' },
    { Id: 2, Name: 'No' }
  ];

  const [state, setState] = useState({
    id: 0,
    formData: {
      Name: "",
      CouponCount: "",
      Type: "",
      Value: "",
      MaxDiscountAmount: "",
      MinOrderAmount: "",
      ValidTillDate: "",
      Remarks: "",
      IsAvailableForAll: "",
    },
  });

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const nameRef = useRef(null);

  useEffect(() => {
    const Id = (location.state && location.state.Id) || 0;
    if (Id > 0) {
      setState((prevState) => ({ ...prevState, id: Id }));
      Fn_DisplayData(dispatch, setState, Id, API_URL_EDIT);
    }
  }, [dispatch, location.state]);

  // Auto-focus on Name field when component mounts
  useEffect(() => {
    if (nameRef.current) {
      nameRef.current.focus();
    }
  }, []);

  // Validation Schema
  const validationSchema = Yup.object({
    Name: Yup.string()
      .required("Coupon name is required")
      .min(2, "Coupon name must be at least 2 characters")
      .max(500, "Coupon name must be less than 500 characters"),
    CouponCount: Yup.number()
      .typeError("Coupon count must be a valid number")
      .required("Coupon count is required")
      .min(1, "Coupon count must be at least 1")
      .integer("Coupon count must be a whole number"),
    Type: Yup.string()
      .required("Type is required"),
    Value: Yup.number()
      .typeError("Value must be a valid number")
      .required("Value is required")
      .min(0, "Value cannot be negative")
      .when("Type", {
        is: "2", // Percent
        then: (schema) => schema.max(100, "Percentage value cannot exceed 100"),
        otherwise: (schema) => schema,
      }),
    MaxDiscountAmount: Yup.number()
      .typeError("Max discount amount must be a valid number")
      .nullable()
      .min(0, "Max discount amount cannot be negative"),
    MinOrderAmount: Yup.number()
      .typeError("Min order amount must be a valid number")
      .nullable()
      .min(0, "Min order amount cannot be negative"),
    ValidTillDate: Yup.date()
      .nullable()
      .typeError("Please enter a valid date"),
    Remarks: Yup.string()
      .nullable()
      .max(1000, "Remarks must be less than 1000 characters"),
    IsAvailableForAll: Yup.string()
      .required("Is Available For All is required"),
  });

  const handleSubmit = async (values) => {
    const obj = JSON.parse(localStorage.getItem("user"));
    let formData = new FormData();
    formData.append("Name", values.Name || "");
    formData.append("CouponCount", values.CouponCount ?? 0);
    formData.append("Type", values.Type || "");
    formData.append("Value", values.Value ?? 0);
    formData.append("MaxDiscountAmount", values.MaxDiscountAmount ?? "");
    formData.append("MinOrderAmount", values.MinOrderAmount ?? "");
    formData.append("ValidTillDate", values.ValidTillDate || "");
    formData.append("Remarks", values.Remarks || "");
    formData.append("IsAvailableForAll", values.IsAvailableForAll || "");
    formData.append("UserId", obj === null || obj === undefined ? 0 : obj.uid);

    Fn_AddEditData(
      dispatch,
      setState,
      { arguList: { id: state.id, formData } },
      API_URL_SAVE,
      true,
      "couponmasterid",
      navigate,
      "/CouponMaster"
    );
  };

  return (
    <div className="master-form-container" style={{padding: '10px 0'}}>
      <div className="container-fluid px-2">
        <Breadcrumbs title={breadCrumbTitle} breadcrumbItem={breadcrumbItem} />
        
        {/* Form Card */}
        <Row className="justify-content-center">
          <Col lg={8} md={12} sm={12}>
            <Card className="master-form-card master-form-slide-in">
              <CardHeader className="master-form-card-header" style={{padding: '12px 20px'}}>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <div className="form-icon-wrapper me-2">
                      <Settings size={18} color="#6D68CB" />
                    </div>
                    <div>
                      <h5 className="mb-0 fw-bold">Coupon Master</h5>
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardBody className="master-form-card-body">
                <Formik 
                  initialValues={{
                    Name: state.formData?.Name ?? "",
                    CouponCount: state.formData?.CouponCount ?? "",
                    Type: state.formData?.Type ?? "",
                    Value: state.formData?.Value ?? "",
                    MaxDiscountAmount: state.formData?.MaxDiscountAmount ?? "",
                    MinOrderAmount: state.formData?.MinOrderAmount ?? "",
                    ValidTillDate: state.formData?.ValidTillDate ? formatDateYYYYMMDD(state.formData.ValidTillDate) : "",
                    Remarks: state.formData?.Remarks ?? "",
                    IsAvailableForAll: state.formData?.IsAvailableForAll ?? "",
                  }} 
                  validationSchema={validationSchema} 
                  onSubmit={handleSubmit}
                  enableReinitialize={true}
                >
                  {({ isSubmitting, errors, touched, values }) => (
                    <Form>
                      <div className="form-group mb-3">
                        <label htmlFor="Name" className="master-form-label">
                          <Tag size={16} className="me-2" />
                          Coupon Name <span className="required">*</span>
                        </label>
                        <div className="input-wrapper">
                          <Field
                            innerRef={nameRef}
                            className={`master-form-input ${errors.Name && touched.Name ? 'is-invalid' : ''}`}
                            type="text"
                            name="Name"
                            placeholder="Enter coupon name"
                          />
                          <div className="input-icon">
                            <Tag size={16} color="#6c757d" />
                          </div>
                        </div>
                        <ErrorMessage name="Name" component="div" className="master-form-error" />
                        <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                          <i className="fas fa-lightbulb me-1"></i>
                          Enter a unique name for this coupon
                        </div>
                      </div>

                      <div className="form-group mb-3">
                        <label htmlFor="CouponCount" className="master-form-label">
                          <Tag size={16} className="me-2" />
                          Coupon Count <span className="required">*</span>
                        </label>
                        <div className="input-wrapper">
                          <Field
                            className={`master-form-input ${errors.CouponCount && touched.CouponCount ? 'is-invalid' : ''}`}
                            type="number"
                            name="CouponCount"
                            placeholder="Enter coupon count"
                            min="1"
                          />
                          <div className="input-icon">
                            <Tag size={16} color="#6c757d" />
                          </div>
                        </div>
                        <ErrorMessage name="CouponCount" component="div" className="master-form-error" />
                        <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                          <i className="fas fa-lightbulb me-1"></i>
                          Enter the number of coupons available
                        </div>
                      </div>

                      <div className="form-group mb-3">
                        <label htmlFor="Type" className="master-form-label">
                          <Settings size={16} className="me-2" />
                          Type <span className="required">*</span>
                        </label>
                        <div className="input-wrapper">
                          <Field
                            as="select"
                            className={`master-form-input ${errors.Type && touched.Type ? 'is-invalid' : ''}`}
                            name="Type"
                          >
                            <option value="">Select Type</option>
                            {typeOptions.map((option) => (
                              <option key={option.Id} value={option.Id}>
                                {option.Name}
                              </option>
                            ))}
                          </Field>
                          <div className="input-icon">
                            <Settings size={16} color="#6c757d" />
                          </div>
                        </div>
                        <ErrorMessage name="Type" component="div" className="master-form-error" />
                        <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                          <i className="fas fa-lightbulb me-1"></i>
                          Select Flat for fixed amount or Percent for percentage discount
                        </div>
                      </div>

                      <div className="form-group mb-3">
                        <label htmlFor="IsAvailableForAll" className="master-form-label">
                          <Settings size={16} className="me-2" />
                          Is Available For All <span className="required">*</span>
                        </label>
                        <div className="input-wrapper">
                          <Field
                            as="select"
                            className={`master-form-input ${errors.IsAvailableForAll && touched.IsAvailableForAll ? 'is-invalid' : ''}`}
                            name="IsAvailableForAll"
                          >
                            <option value="">Select Option</option>
                            {isAvailableForAllOptions.map((option) => (
                              <option key={option.Id} value={option.Id}>
                                {option.Name}
                              </option>
                            ))}
                          </Field>
                          <div className="input-icon">
                            <Settings size={16} color="#6c757d" />
                          </div>
                        </div>
                        <ErrorMessage name="IsAvailableForAll" component="div" className="master-form-error" />
                        <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                          <i className="fas fa-lightbulb me-1"></i>
                          Select Yes if coupon is available for all users, No otherwise
                        </div>
                      </div>

                      <div className="form-group mb-3">
                        <label htmlFor="Value" className="master-form-label">
                          <DollarSign size={16} className="me-2" />
                          Value <span className="required">*</span>
                          {values.Type === "2" && <span className="text-muted ms-2">(Max: 100%)</span>}
                        </label>
                        <div className="input-wrapper">
                          <Field
                            className={`master-form-input ${errors.Value && touched.Value ? 'is-invalid' : ''}`}
                            type="number"
                            name="Value"
                            placeholder={values.Type === "2" ? "Enter percentage (0-100)" : "Enter value"}
                            min="0"
                            max={values.Type === "2" ? "100" : undefined}
                            step={values.Type === "2" ? "0.01" : "0.01"}
                          />
                          <div className="input-icon">
                            <DollarSign size={16} color="#6c757d" />
                          </div>
                        </div>
                        <ErrorMessage name="Value" component="div" className="master-form-error" />
                        <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                          <i className="fas fa-lightbulb me-1"></i>
                          {values.Type === "2" 
                            ? "Enter the discount percentage (0-100)"
                            : "Enter the flat discount amount"}
                        </div>
                      </div>

                      <Row className="g-3">
                        <Col md={6}>
                          <div className="form-group mb-3 mb-md-0">
                            <label htmlFor="MaxDiscountAmount" className="master-form-label">
                              <DollarSign size={16} className="me-2" />
                              Max Discount Amount
                            </label>
                            <div className="input-wrapper">
                              <Field
                                className={`master-form-input ${errors.MaxDiscountAmount && touched.MaxDiscountAmount ? 'is-invalid' : ''}`}
                                type="number"
                                name="MaxDiscountAmount"
                                placeholder="Enter max discount amount"
                                min="0"
                                step="0.01"
                              />
                              <div className="input-icon">
                                <DollarSign size={16} color="#6c757d" />
                              </div>
                            </div>
                            <ErrorMessage name="MaxDiscountAmount" component="div" className="master-form-error" />
                            <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                              <i className="fas fa-lightbulb me-1"></i>
                              Maximum discount amount allowed (optional)
                            </div>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="form-group">
                            <label htmlFor="MinOrderAmount" className="master-form-label">
                              <DollarSign size={16} className="me-2" />
                              Min Order Amount
                            </label>
                            <div className="input-wrapper">
                              <Field
                                className={`master-form-input ${errors.MinOrderAmount && touched.MinOrderAmount ? 'is-invalid' : ''}`}
                                type="number"
                                name="MinOrderAmount"
                                placeholder="Enter minimum order amount"
                                min="0"
                                step="0.01"
                              />
                              <div className="input-icon">
                                <DollarSign size={16} color="#6c757d" />
                              </div>
                            </div>
                            <ErrorMessage name="MinOrderAmount" component="div" className="master-form-error" />
                            <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                              <i className="fas fa-lightbulb me-1"></i>
                              Minimum order amount required to use this coupon (optional)
                            </div>
                          </div>
                        </Col>
                      </Row>

                      <div className="form-group mb-3">
                        <label htmlFor="ValidTillDate" className="master-form-label">
                          <Calendar size={16} className="me-2" />
                          Valid Till Date
                        </label>
                        <div className="input-wrapper">
                          <Field
                            className={`master-form-input ${errors.ValidTillDate && touched.ValidTillDate ? 'is-invalid' : ''}`}
                            type="date"
                            name="ValidTillDate"
                          />
                          <div className="input-icon">
                            <Calendar size={16} color="#6c757d" />
                          </div>
                        </div>
                        <ErrorMessage name="ValidTillDate" component="div" className="master-form-error" />
                        <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                          <i className="fas fa-lightbulb me-1"></i>
                          Coupon expiry date (optional)
                        </div>
                      </div>

                      <div className="form-group mb-3">
                        <label htmlFor="Remarks" className="master-form-label">
                          <FileText size={16} className="me-2" />
                          Remarks
                        </label>
                        <div className="input-wrapper">
                          <Field
                            as="textarea"
                            className={`master-form-input ${errors.Remarks && touched.Remarks ? 'is-invalid' : ''}`}
                            name="Remarks"
                            placeholder="Enter remarks (optional)"
                            rows="3"
                          />
                          <div className="input-icon">
                            <FileText size={16} color="#6c757d" />
                          </div>
                        </div>
                        <ErrorMessage name="Remarks" component="div" className="master-form-error" />
                        <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                          <i className="fas fa-lightbulb me-1"></i>
                          Additional notes or remarks about this coupon (optional)
                        </div>
                      </div>

                      <div className="master-form-actions mt-4">
                        <div className="d-flex gap-2 justify-content-center">
                          <Button
                            type="button"
                            className="master-form-cancel-btn"
                            onClick={() => navigate("/CouponMaster")}
                            variant="outline-secondary"
                            size="md"
                          >
                            <ArrowLeft size={16} className="me-1" />
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            className="master-form-submit-btn"
                            disabled={isSubmitting}
                            size="md"
                          >
                            {isSubmitting ? (
                              <div className="master-form-loading me-1" />
                            ) : (
                              <Save size={16} className="me-1" />
                            )}
                            {state.id > 0 ? 'Update Coupon' : 'Save Coupon'}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default AddEdit_CouponMaster;

